import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { subscriptionsApi } from '../api/subscriptionsApi';
import { getSiteOrigin, joinUrl } from '../utils/seo';

const BILLING_MODE_MONTHLY = 'monthly';
const BILLING_MODE_ANNUAL = 'annual';

function toAmount(value) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? Math.max(0, numberValue) : 0;
}

function formatMoney(value) {
  return new Intl.NumberFormat('ru-RU').format(Math.round(toAmount(value)));
}

function isFreePlan(plan) {
  return toAmount(plan?.monthlyPrice) <= 0;
}

function sortPlans(plans = []) {
  return [...plans].sort((left, right) => {
    const leftFree = isFreePlan(left);
    const rightFree = isFreePlan(right);
    if (leftFree !== rightFree) return leftFree ? -1 : 1;

    const monthlyDiff = toAmount(left?.monthlyPrice) - toAmount(right?.monthlyPrice);
    if (monthlyDiff !== 0) return monthlyDiff;

    return String(left?.name || '').localeCompare(String(right?.name || ''), 'ru');
  });
}

function planDisplayPrice(plan, billingMode) {
  const monthlyPrice = toAmount(plan?.monthlyPrice);
  const yearlyTotal = toAmount(plan?.yearlyTotal);
  if (billingMode === BILLING_MODE_ANNUAL && yearlyTotal > 0) {
    return Math.max(0, Math.round(yearlyTotal / 12));
  }
  return Math.round(monthlyPrice);
}

function planPeriodLabel(plan, billingMode) {
  if (isFreePlan(plan)) return 'навсегда';
  if (billingMode === BILLING_MODE_ANNUAL && toAmount(plan?.yearlyTotal) > 0) {
    return '/мес при оплате за год';
  }
  return '/месяц';
}

function planSavingsAmount(plan) {
  const monthlyPrice = toAmount(plan?.monthlyPrice);
  const yearlyTotal = toAmount(plan?.yearlyTotal);
  if (monthlyPrice <= 0 || yearlyTotal <= 0) return 0;
  return Math.max(0, Math.round(monthlyPrice * 12 - yearlyTotal));
}

function planNote(plan, billingMode) {
  if (isFreePlan(plan)) return 'Без карты, без срока действия';

  const yearlyTotal = Math.round(toAmount(plan?.yearlyTotal));
  const savings = planSavingsAmount(plan);

  if (billingMode === BILLING_MODE_ANNUAL && yearlyTotal > 0) {
    return savings > 0
      ? `Итого ${formatMoney(yearlyTotal)} ₽/год · экономия ${formatMoney(savings)} ₽`
      : `Итого ${formatMoney(yearlyTotal)} ₽/год`;
  }

  if (yearlyTotal > 0) {
    return savings > 0
      ? `Или ${formatMoney(yearlyTotal)} ₽/год · экономия ${formatMoney(savings)} ₽`
      : `Или ${formatMoney(yearlyTotal)} ₽/год`;
  }

  return '7 дней бесплатно на платном тарифе';
}

function planButtonLabel(plan) {
  return isFreePlan(plan) ? 'Начать бесплатно →' : 'Попробовать 7 дней →';
}

function planButtonTone(plan) {
  if (plan?.isPopular) return 'bright';
  return isFreePlan(plan) ? 'light' : 'light';
}

function normalizeFeatureEntries(plan) {
  const rawFeatures = Array.isArray(plan?.features) ? plan.features : [];
  return rawFeatures
    .map((feature) => {
      const source = feature && typeof feature === 'object' ? feature : {};
      const label = String(source.label || source.title || source.name || '').trim();
      if (!label) return null;

      const separatorIndex = label.indexOf(':');
      const key = separatorIndex >= 0 ? label.slice(0, separatorIndex).trim() : label;
      const value = separatorIndex >= 0 ? label.slice(separatorIndex + 1).trim() : '';

      return {
        key: key || label,
        label,
        value: value || (source.enabled === false ? '—' : '✓'),
        enabled: source.enabled !== false,
      };
    })
    .filter(Boolean);
}

export default function Pricing() {
  const [billingMode, setBillingMode] = useState(BILLING_MODE_MONTHLY);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadPlans = async () => {
      try {
        const nextPlans = await subscriptionsApi.listPlans();
        if (!cancelled) {
          setPlans(Array.isArray(nextPlans) ? nextPlans : []);
        }
      } catch {
        if (!cancelled) {
          setPlans([]);
        }
      } finally {
        if (!cancelled) {
          setPlansLoading(false);
        }
      }
    };

    loadPlans();
    return () => {
      cancelled = true;
    };
  }, []);

  const origin = useMemo(() => getSiteOrigin(), []);
  const canonical = useMemo(() => joinUrl(origin, '/pricing'), [origin]);
  const orderedPlans = useMemo(() => sortPlans(plans), [plans]);
  const paidPlans = useMemo(() => orderedPlans.filter((plan) => !isFreePlan(plan)), [orderedPlans]);
  const featureRows = useMemo(() => {
    const rows = [];
    const seen = new Set();
    const featureMaps = orderedPlans.map((plan) => ({
      code: String(plan?.code || ''),
      isPopular: Boolean(plan?.isPopular),
      entries: normalizeFeatureEntries(plan),
    }));

    featureMaps.forEach(({ entries }) => {
      entries.forEach((entry) => {
        if (seen.has(entry.key)) return;
        seen.add(entry.key);
        rows.push({
          key: entry.key,
          values: featureMaps.map((map) => {
            const matched = map.entries.find((item) => item.key === entry.key);
            return matched
              ? { text: matched.value, enabled: matched.enabled, isPopular: map.isPopular }
              : { text: '—', enabled: false, isPopular: map.isPopular };
          }),
        });
      });
    });

    return rows;
  }, [orderedPlans]);

  const title = 'Тарифы — Бюро Души';
  const description = 'Прозрачные тарифы для психологов: фиксированная подписка, 0% комиссии с оплат клиентов, полный доступ к платформе.';

  return (
    <div className="b-pricing-page">
      <Helmet prioritizeSeoTags>
        <title>{title}</title>
        <meta name="description" content={description} />
        {canonical ? <link rel="canonical" href={canonical} /> : null}
        <meta name="robots" content="index, follow" />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content="ru_RU" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {canonical ? <meta property="og:url" content={canonical} /> : null}

        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>

      <section className="pricing-hero">
        <div className="pricing-hero__inner">
          <div className="pricing-hero__label">✦ Тарифы</div>
          <h1 className="pricing-hero__title">
            Прозрачно.
            <br />
            <span className="m">Без комиссий.</span>
            <br />
            <span className="l">Ваш доход — ваш.</span>
          </h1>
          <p className="pricing-hero__sub">
            Фиксированная подписка и 100% оплат от клиентов поступают вам.
            Никаких скрытых платежей и процентов с сессий.
          </p>

          <div className="billing-toggle" role="tablist" aria-label="Режим тарифа">
            <button
              type="button"
              className={`billing-toggle__option ${billingMode === BILLING_MODE_MONTHLY ? 'active' : ''}`}
              onClick={() => setBillingMode(BILLING_MODE_MONTHLY)}
              role="tab"
              aria-selected={billingMode === BILLING_MODE_MONTHLY}
            >
              Ежемесячно
            </button>
            <button
              type="button"
              className={`billing-toggle__option ${billingMode === BILLING_MODE_ANNUAL ? 'active' : ''}`}
              onClick={() => setBillingMode(BILLING_MODE_ANNUAL)}
              role="tab"
              aria-selected={billingMode === BILLING_MODE_ANNUAL}
            >
              Ежегодно <span className="billing-toggle__save">−10%</span>
            </button>
          </div>
        </div>
      </section>

      <section className="pricing-cards">
        <div className="pricing-cards__grid">
          {!orderedPlans.length ? (
            <article className="price-card">
              <div className="price-card__label">Тарифы</div>
              <h2 className="price-card__name">Загружаем актуальные планы</h2>
              <p className="price-card__tagline">Подключаем опубликованные тарифы с сервера.</p>
              <div className="price-card__divider" />
              <div className="price-card__note">Если публикация уже включена на бэке, цены появятся здесь автоматически.</div>
            </article>
          ) : null}

          {orderedPlans.map((plan, index) => {
            const displayPrice = planDisplayPrice(plan, billingMode);
            const features = normalizeFeatureEntries(plan);
            return (
              <article
                key={String(plan?.code || plan?.name || `plan-${index}`)}
                className={`price-card ${plan?.isPopular ? 'popular' : ''}`}
              >
                {plan?.isPopular ? <div className="price-card__popular-badge">Популярный выбор</div> : null}
                <div className="price-card__label">Тариф</div>
                <h2 className="price-card__name">{plan?.name || 'Тариф'}</h2>
                <p className="price-card__tagline">
                  {plan?.description || (isFreePlan(plan) ? 'Чтобы попробовать без риска' : 'Подходит для регулярной практики')}
                </p>
                <div className="price-card__divider" />
                <div className="price-card__amount-row">
                  <span className="price-card__currency">₽</span>
                  <span className="price-card__amount">{formatMoney(displayPrice)}</span>
                </div>
                <div className="price-card__period">{planPeriodLabel(plan, billingMode)}</div>
                <div className="price-card__note">{planNote(plan, billingMode)}</div>
                <Link className={`price-card__button ${planButtonTone(plan)}`} to="/login">{planButtonLabel(plan)}</Link>
                <ul className="price-card__features">
                  {features.map((feature) => (
                    <li key={`${plan?.code || 'plan'}:${feature.key}`}>
                      <span className={feature.enabled ? `check ${plan?.isPopular ? 'dark' : isFreePlan(plan) ? 'mint' : 'lav'}` : 'dash'}>
                        {feature.enabled ? '✓' : '—'}
                      </span>
                      {feature.label}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        <div className="pricing-cards__note">
          {plansLoading
            ? 'Загружаем актуальные тарифы…'
            : paidPlans.length
              ? 'Тарифы и лимиты подгружаются из актуальных планов платформы.'
              : 'Платные тарифы появятся здесь после публикации на сервере.'}
        </div>
      </section>

      <section className="pricing-compare">
        <h2 className="pricing-compare__title">
          Подробное <span className="m">сравнение</span>
        </h2>
        <p className="pricing-compare__sub">Посмотрите, что входит в каждый тариф — по функциям, без маркетинга.</p>

        <div className="pricing-compare__table-wrap">
          {orderedPlans.length ? (
            <table className="pricing-compare__table">
              <thead>
                <tr>
                  <th>Функция</th>
                  {orderedPlans.map((plan) => (
                    <th key={`head:${plan?.code || plan?.name}`} className={plan?.isPopular ? 'popular-col' : ''}>
                      {plan?.name || 'Тариф'}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="section-row"><td colSpan={Math.max(2, orderedPlans.length + 1)}>Актуальные лимиты и возможности</td></tr>
                {featureRows.map((row) => (
                  <tr key={`row:${row.key}`}>
                    <td>{row.key}</td>
                    {row.values.map((value, index) => (
                      <td
                        key={`row:${row.key}:col:${index}`}
                        className={value.isPopular ? 'popular-col' : ''}
                      >
                        {value.enabled && value.text === '✓'
                          ? <span className="check-y">✓</span>
                          : !value.enabled || value.text === '—'
                            ? <span className="check-n">—</span>
                            : value.text}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
        <p className="pricing-compare__scroll-hint">Смахните таблицу влево, чтобы увидеть все тарифы →</p>
      </section>

      <section className="no-commission">
        <div className="no-commission__grid">
          <div>
            <div className="no-commission__label">Почему 0% комиссии</div>
            <h2 className="no-commission__title">
              Мы зарабатываем
              <br />
              на подписке —
              <br />
              не на <span className="m">вашем доходе</span>
            </h2>
            <p className="no-commission__desc">
              Большинство платформ берут 15–30% с каждой оплаты клиента.
              Мы считаем это несправедливым. Вы ведёте работу — вы получаете всё.
            </p>

            <div className="no-commission__example">
              <div className="ex-title">Ваш расчёт за месяц</div>
              <div className="ex-row"><span>30 сессий × 4 000 ₽</span><strong>120 000 ₽</strong></div>
              <div className="ex-row"><span>Подписка Базовый</span><strong className="red">−890 ₽</strong></div>
              <div className="ex-row"><span>Комиссия с оплат</span><strong className="mint">0 ₽</strong></div>
              <div className="ex-total"><span>Ваш чистый доход</span><strong>119 110 ₽</strong></div>
            </div>
          </div>

          <div>
            <div className="vs-title">Сравнение с конкурентами</div>
            <div className="vs-list">
              <div className="vs-item highlighted">
                <div className="vs-name">Бюро Души CRM</div>
                <div className="vs-calc">120 000 ₽ − 890 ₽ (подписка) − 0 ₽ (комиссия)</div>
                <div className="vs-result mint">= 119 110 ₽</div>
              </div>
              <div className="vs-item">
                <div className="vs-name">Конкурент А (20% комиссия)</div>
                <div className="vs-calc">120 000 ₽ − 0 ₽ (подписка) − 24 000 ₽ (20%)</div>
                <div className="vs-result red">= 96 000 ₽</div>
              </div>
              <div className="vs-item">
                <div className="vs-name">Конкурент Б (15% + подписка)</div>
                <div className="vs-calc">120 000 ₽ − 2 000 ₽ (подписка) − 18 000 ₽ (15%)</div>
                <div className="vs-result red">= 100 000 ₽</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pricing-trust">
        <div className="pricing-trust__head">
          <div className="head-label">Гарантии</div>
          <h2 className="head-title">Что мы <span>гарантируем</span></h2>
          <p className="head-sub">На любом тарифе, без дополнительных условий и мелкого шрифта.</p>
        </div>

        <div className="pricing-trust__grid">
          <article className="trust-card">
            <span className="trust-icon">↻</span>
            <h3>Отмена в любой момент</h3>
            <p>Никаких штрафов. Отменяете подписку — и больше ничего не списывается.</p>
          </article>
          <article className="trust-card">
            <span className="trust-icon">☁</span>
            <h3>Данные остаются у вас</h3>
            <p>После отмены вы можете скачать все данные в течение 30 дней.</p>
          </article>
          <article className="trust-card">
            <span className="trust-icon">🎁</span>
            <h3>7 дней без карты</h3>
            <p>Пробный период без ввода карты. Переходите на платный только сами.</p>
          </article>
          <article className="trust-card">
            <span className="trust-icon">🔒</span>
            <h3>Цена не меняется</h3>
            <p>Зафиксированная цена при оформлении. Повышения без предупреждения — никогда.</p>
          </article>
        </div>
      </section>

      <section className="pricing-faq">
        <div className="pricing-faq__head">
          <div className="head-label">Вопросы о тарифах</div>
          <h2 className="head-title">Часто <span>спрашивают</span></h2>
        </div>

        <div className="pricing-faq__grid">
          <article className="faq-card"><h3>Можно ли перейти на другой тариф?</h3><p>Да, в любой момент. Переход на более высокий тариф активируется сразу, переход вниз — с начала следующего расчётного периода.</p></article>
          <article className="faq-card"><h3>Что будет после окончания пробного периода?</h3><p>Мы уведомим вас за 3 дня до окончания 7 дней. Если тариф не выбран — аккаунт перейдёт на бесплатный план, данные сохранятся.</p></article>
          <article className="faq-card"><h3>Как работает ежегодная оплата?</h3><p>Вы платите за год сразу со скидкой 10%. Базовый — 9 590 ₽, расширенный — 15 990 ₽ вместо полной стоимости.</p></article>
          <article className="faq-card"><h3>Какие способы оплаты подписки?</h3><p>Банковская карта и СБП. Аккаунт принадлежит одному специалисту — корпоративных тарифов пока нет.</p></article>
          <article className="faq-card"><h3>Сколько психологов может работать на одном аккаунте?</h3><p>Один аккаунт — один специалист. Каждый психолог регистрируется отдельно. Командных и центровых тарифов пока нет.</p></article>
          <article className="faq-card"><h3>Можно ли отменить подписку?</h3><p>Да, в любой момент. После отмены тариф действует до конца оплаченного периода, затем аккаунт переходит на бесплатный.</p></article>
        </div>
      </section>

      <section className="pricing-cta">
        <div className="pricing-cta__inner">
          <h2>
            Начните с <span>7 дней</span>
            <br />
            бесплатно
          </h2>
          <p>Выберите любой тариф. Пробный период без карты, дальше решите продолжать или нет.</p>
          <Link className="pricing-cta__btn" to="/login">Попробовать бесплатно →</Link>
          <div className="pricing-cta__note">Или напишите нам: <a href="mailto:support@burosoul.ru">support@burosoul.ru</a></div>
        </div>
      </section>
    </div>
  );
}
